
import { useEffect, useState, useRef } from 'react';

interface CounterProps {
  end: number;
  suffix?: string;
  prefix?: string;
  duration?: number;
}

function AnimatedCounter({ end, suffix = '', prefix = '', duration = 2000 }: CounterProps) {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started) {
          setStarted(true);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    let startTime: number | null = null;
    const animate = (timestamp: number) => {
      if (startTime === null) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [started, end, duration]);

  return (
    <div ref={ref} className="font-playfair text-[40px] md:text-[56px] font-bold text-[#2d3e40] leading-none">
      {prefix}{count}{suffix}
    </div>
  );
}

export default function StatisticheSection() {
  const stats = [
    {
      end: 40,
      suffix: '%',
      title: 'Emissioni Globali',
      desc: "degli edifici contribuiscono alle emissioni globali di CO\u2082. L'architettura sostenibile è la risposta urgente.",
      icon: 'ri-cloud-line',
    },
    {
      end: 80,
      suffix: '%',
      title: 'Risparmio Energetico',
      desc: 'di riduzione del consumo energetico nei nostri progetti grazie a sistemi passivi e materiali bio-climatici.',
      icon: 'ri-flashlight-line',
    },
    {
      end: 150,
      suffix: '+',
      title: 'Progetti Sostenibili',
      desc: 'edifici progettati con certificazione ambientale, trasformando il paesaggio urbano italiano.',
      icon: 'ri-building-2-line',
    },
    {
      end: 95,
      suffix: '%',
      title: 'Materiali Naturali',
      desc: 'dei materiali utilizzati nei nostri progetti proviene da fonti rinnovabili, riciclate o a km zero.',
      icon: 'ri-recycle-line',
    },
  ];

  return (
    <section id="statistiche" className="py-16 md:py-32 bg-[#2d3e40] relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-[400px] md:w-[600px] h-[400px] md:h-[600px] rounded-full border border-white/20 -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-[500px] md:w-[800px] h-[500px] md:h-[800px] rounded-full border border-white/20 translate-x-1/3 translate-y-1/3"></div>
      </div>

      <div className="max-w-[1400px] mx-auto px-5 md:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-12 md:mb-20">
          <div className="flex items-center justify-center gap-3 mb-5">
            <div className="w-8 h-[1px] bg-[#6b8e7f]"></div>
            <span className="font-montserrat text-[10px] md:text-[11px] uppercase tracking-[3px] md:tracking-[4px] text-[#6b8e7f]">
              L&apos;Impatto dell&apos;Architettura
            </span>
            <div className="w-8 h-[1px] bg-[#6b8e7f]"></div>
          </div>
          <h2 className="font-playfair text-[32px] md:text-[52px] font-bold text-white leading-[1.15]">
            Numeri che Parlano di<br className="hidden md:block" />{' '}
            <em className="text-[#8fb5a3] not-italic">Sostenibilità</em>
          </h2>
          <p className="font-montserrat text-[13px] md:text-[15px] text-white/50 mt-4 md:mt-6 max-w-[600px] mx-auto leading-[1.8]">
            L&apos;architettura è responsabile di quasi la metà delle emissioni globali. Ogni progetto
            sostenibile è un passo verso un futuro migliore.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {stats.map((stat, i) => (
            <div
              key={i}
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 md:p-8 text-center hover:bg-white/10 transition-all duration-500 group"
            >
              <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-[#6b8e7f]/20 flex items-center justify-center mx-auto mb-4 md:mb-6 group-hover:bg-[#6b8e7f]/30 transition-colors duration-500">
                <i className={`${stat.icon} text-[#8fb5a3] text-xl md:text-2xl`}></i>
              </div>
              <AnimatedCounter end={stat.end} suffix={stat.suffix} />
              <h3 className="font-montserrat text-[11px] md:text-[13px] font-semibold text-white uppercase tracking-[1px] mt-3 md:mt-4 mb-2 md:mb-3">
                {stat.title}
              </h3>
              <p className="font-montserrat text-[11px] md:text-[13px] text-white/45 leading-[1.7]">
                {stat.desc}
              </p>
            </div>
          ))}
        </div>

        {/* Full Width Image Banner */}
        <div className="mt-12 md:mt-20 rounded-2xl overflow-hidden relative h-[200px] md:h-[300px]">
          <img
            src="https://public.readdy.ai/ai/img_res/62acffc918336f96b6e9287cff445f58.jpg"
            alt="Città sostenibile panoramica"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#2d3e40]/90 via-[#2d3e40]/60 to-transparent md:from-[#2d3e40]/80 md:via-[#2d3e40]/40"></div>
          <div className="absolute left-5 md:left-12 top-1/2 -translate-y-1/2 max-w-[280px] md:max-w-[500px]">
            <p className="font-playfair italic text-[16px] md:text-[24px] text-white leading-[1.5]">
              &ldquo;L&apos;architettura sostenibile non è un lusso, è una necessità per le generazioni future.&rdquo;
            </p>
            <span className="font-montserrat text-[10px] md:text-[12px] text-white/50 mt-3 md:mt-4 block uppercase tracking-[2px]">
              — Marco Incitti
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
